import { Card } from "@/components/ui/card";
import type { ExperienceEntry } from "@/types/site";

interface ExperienceSectionProps {
  items: ExperienceEntry[];
}

/**
 * Presents professional experience as a vertical timeline with a clear visual
 * sequence and concise bullet points.
 */
export function ExperienceSection({ items }: ExperienceSectionProps): JSX.Element {
  return (
    <section className="pb-20">
      <div className="mx-auto max-w-5xl px-6 lg:px-10">
        <div className="relative pl-8 before:absolute before:bottom-0 before:left-3 before:top-0 before:w-px before:bg-border/30 md:pl-10 md:before:left-4">
          {items.map((item, index) => (
            <article
              className="relative pb-10 last:pb-0"
              key={`${item.company}-${item.period}`}
            >
              <span
                aria-hidden="true"
                className="absolute left-[-1.85rem] top-8 z-10 size-3 rounded-full border-4 border-background bg-primary md:left-[-2.35rem]"
              />
              <div className="mb-4 space-y-1">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary/90">
                  {item.period}
                </p>
                <p className="text-sm leading-6 text-spotify-silver">
                  {item.location}
                </p>
              </div>
              <Card className="border border-border/20 bg-spotify-surface/75 p-6 md:p-8">
                <div className="space-y-6">
                  <header className="space-y-3">
                    <h2 className="text-2xl font-semibold text-foreground md:text-3xl">
                      {item.role}
                    </h2>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-base text-spotify-silver">
                      <div className="flex items-center gap-3">
                        {item.icon ? (
                          <div className="flex size-10 shrink-0 items-center justify-center rounded-full-pill border border-border/20 bg-spotify-surface-light p-2">
                            <img
                              alt={`${item.company} logo`}
                              className="size-full object-contain"
                              src={item.icon}
                            />
                          </div>
                        ) : null}
                        <p className="font-semibold text-foreground">
                          {item.company}
                        </p>
                      </div>
                      {item.team ? (
                        <>
                          <span aria-hidden="true" className="text-border/60">
                            /
                          </span>
                          <p>{item.team}</p>
                        </>
                      ) : null}
                    </div>
                  </header>
                  <ul className="space-y-4">
                    {item.contributions.map((contribution) => (
                      <li
                        className="relative pl-6 text-base leading-8 text-foreground/82"
                        key={contribution}
                      >
                        <span
                          aria-hidden="true"
                          className="absolute left-0 top-3 size-2 rounded-full bg-primary"
                        />
                        {contribution}
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
              {index < items.length - 1 ? (
                <div className="mt-10 h-px bg-border/20" />
              ) : null}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
